import { EmployeeAssignmentDetailModel } from 'api/models';
import { getAssignedFormattedEmployee } from 'pages/PostInspectionFlow/utils';
import { EMPLOYEE_TITLES } from 'strings/common.strings';
import {
  FeatureOwner,
  MaintenanceAssessment,
  MaintenanceBundleType,
  MaintenanceResponsibility,
  ConsentStatus,
} from '../../../../models/enums';

const hasRequiredWork = (bundle: any) =>
  bundle.items?.some(
    (item: any) =>
      item.maintenance?.assessment === MaintenanceAssessment.Required,
  ) ?? false;

/**
 * Splits the walkthrough's bundles into the design's two sections. A bundle
 * carries no assessment of its own, so it counts as required when any of its
 * work has to happen before the move-in.
 */
export const partitionBundlesByAssessment = (bundles: any[]) => ({
  required: bundles.filter(hasRequiredWork),
  recommended: bundles.filter((bundle) => !hasRequiredWork(bundle)),
});

export const BUNDLE_CONFIG = {
  CodeCompliance: {
    displayName: 'Code Compliance',
  },
  AppliancesAndEquipment: {
    displayName: 'Home Functionality',
  },
  VacancyReduction: {
    displayName: 'Vacancy Reduction',
  },
  Habitability: {
    displayName: 'Habitability & Comfort',
  },
  SafetyAndSecurity: {
    displayName: 'Safety & Security',
  },
  Preventative: {
    displayName: 'Preventative',
  },
  Recommended: {
    displayName: 'Recommended',
  },
  ProjectManagement: {
    displayName: 'Project Management',
  },
};

export const bundleTypeDescription = {
  [MaintenanceBundleType.CodeCompliance]:
    'Satisfy all requirements to legally rent your home in your state. Doing so helps you avoid fees, lawsuits, and other\u00a0complications.',
  [MaintenanceBundleType.AppliancesAndEquipment]:
    'Residents expect all major appliances and features to operate on move-in. Let’s meet those expectations! And if they work on move-in, we can deduct from the security deposit to repair anything not functioning on\u00a0move-out.',
  [MaintenanceBundleType.VacancyReduction]:
    'Based on our experience getting thousands of homes loved, these improvements attract residents and get them to stay longer. The secret is in the details. Small things that make a home more aesthetic and comfortable help it stand\u00a0out.',
  [MaintenanceBundleType.SafetyAndSecurity]:
    'By providing a safe and secure home for your residents, you build trust with\u00a0them.',
  [MaintenanceBundleType.Preventative]:
    'Preventative maintenance saves you money in the long run. Let’s avoid headaches by fixing small issues now before they become major\u00a0repairs.',
};

export const getFlaggedItems = (items: any, role: any) =>
  items.filter(
    (item: any) =>
      item.maintenancePayments.find((payment: any) => payment.paidBy === role)
        ?.flagged,
  );

export const getCalculatedBundles = (bundles: any, homeListing: any) => {
  const { spaces = [] } = homeListing;

  return bundles.map((bundle: any) => {
    return {
      ...bundle,
      items: bundle.items.map((item: any) => {
        const { maintenance } = item || {};
        let roomOrSpace;
        if (maintenance?.ownerType === FeatureOwner.Space) {
          roomOrSpace = spaces.find(
            (space: any) => space.id === maintenance.ownerId,
          );
        }

        const { type } = roomOrSpace || { type: 'Around The Home' };
        return {
          ...item,
          roomOrSpace: type,
        };
      }),
    };
  });
};

export const getPayments = (payments: any) => {
  return {
    homeowner: payments?.find(
      (x: any) => x.paidBy === MaintenanceResponsibility.Homeowner,
    ),
    resident: payments?.find(
      (x: any) => x.paidBy === MaintenanceResponsibility.Resident,
    ),
    belong: payments?.find(
      (x: any) => x.paidBy === MaintenanceResponsibility.Belong,
    ),
  };
};

export const hasResidentResponsibility = (item: any) => {
  const { maintenancePayments } = item;
  const residentPayment = maintenancePayments.find(
    (p: any) => p.paidBy === MaintenanceResponsibility.Resident,
  );

  return residentPayment && residentPayment.percentage > 0;
};

export const getPaymentBreakdown = (bundle: any) => {
  return getPayments(bundle.paymentResponsibilityBreakdown);
};

export const isItemResidentResponsible = (item: any) => {
  const { maintenancePayments } = item;
  const homeOwnerPayment = maintenancePayments.find(
    (breakdown: any) =>
      breakdown.paidBy === MaintenanceResponsibility.Homeowner,
  );

  return !homeOwnerPayment || homeOwnerPayment?.percentage === 0;
};

export const isTBD = (item: any) => {
  return item.isTBD;
};

export const hasHomeownerApproved = (payments: any) => {
  const breakdown = getPayments(payments);
  return breakdown.homeowner?.consentStatus === ConsentStatus.Approved;
};

export const getImprovementsSpecialist = (model: any) => {
  // Annotated: `Object.values` over an untyped map yields `unknown[]`, so every
  // read off the found entry was an error.
  const employee: any = Object.values(model?.employeeAssignments)?.find(
    (employeeAssignmentItem: any) =>
      employeeAssignmentItem.employeeAssignment.assignmentType ===
      'ImprovementsSpecialist',
  );

  return {
    ...employee.employee,
    ...employee.user,
    profileImage: employee.user.profileImageUrl,
    bannerImage: employee.employee.bannerImageUrl,
    jobTitle: 'Home Quality Specialist',
  };
};

export const getImprovementsAssignedEmployee = ({
  employeeAssignments,
  isMoveOut,
}: {
  employeeAssignments: EmployeeAssignmentDetailModel[];
  isMoveOut: boolean;
}) => {
  const assignedEmployee = getAssignedFormattedEmployee({
    employeeAssignments,
    assignmentTypes: [
      ...(isMoveOut ? ['HomeOccupancySpecialist' as const] : []),
      'ImprovementsSpecialist',
    ],
  });

  return {
    ...assignedEmployee,
    jobTitle:
      // `assignment` is optional on the model. Asserting rather than using `?.`
      // keeps today's behaviour exactly: an assignment-less employee threw here
      // and still does, instead of silently taking the second branch.
      assignedEmployee.assignment!.assignmentType === 'HomeOccupancySpecialist'
        ? EMPLOYEE_TITLES.HomeOccupancySpecialist
        : EMPLOYEE_TITLES.HomeImprovementsSpecialist,
  };
};
